import React, {useState, useEffect, useRef, useMemo} from 'react'
import EditNewCard from '../UpdateCard/EditBoardCard'
import 'react-loading-skeleton/dist/skeleton.css'

import {useEditModal} from '../../context/ShowEditModalQuill'
import EditPageModal from '../Modals/EditPageModal'
import { useTheme } from '../../context/ThemeContext'
import { useSelector } from 'react-redux'

const LinkCardUpdate: React.FC = () => {
  const {card, showEditModalQuill, setShowEditModalQuill, cardHistory, goBackInHistory, clearHistory, setCard, expandedFromSource} = useEditModal()
  const {theme} =useTheme()
  const { pagesByBoardId:pages, pagesByLoggedInUser } = useSelector((state: any) => state.pageReducer)
  const { allCurrentUserCards } = useSelector((state: any) => state.cardReducer)

  const [currentCard, setCurrentCard] = useState<any>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  // Counter to absorb spurious closeModal calls during navigation.
  // Forward nav: old card's formik async finally calls closeModal once = 1 spurious call.
  // Back nav: no formik submit, no onHide on unmount (animation=false) = 0 spurious calls.
  const skipCloseCountRef = useRef(0)
  // Track history depth synchronously to avoid stale closure / useEffect-delay issues
  const historyDepthRef = useRef(0)

  // Keep ref in sync when cardHistory changes from outside (e.g. pushToHistory in TextEditor)
  useEffect(() => {
    historyDepthRef.current = cardHistory.length
  }, [cardHistory.length])

  useEffect(() => {
    if (showEditModalQuill === true && card && card.id) {
      skipCloseCountRef.current += 1
      setCurrentCard(card)
      setIsExpanded(expandedFromSource)
      setModalOpen(true)
      setShowEditModalQuill(false)
    }

    return () => {
      setShowEditModalQuill(false)
    }
  }, [showEditModalQuill, card, setShowEditModalQuill, expandedFromSource])

  const closeModal = () => {
    if (skipCloseCountRef.current > 0) {
      skipCloseCountRef.current--
      return
    }
    setModalOpen(false)
    setCurrentCard(null)
    setIsExpanded(false)
    clearHistory()
    historyDepthRef.current = 0
  }

  const handleNavigateBack = () => {
    const previousCard = goBackInHistory()
    if (previousCard) {
      historyDepthRef.current -= 1
      setCurrentCard(previousCard)
      setCard(previousCard)
    } else {
      setModalOpen(false)
      setCurrentCard(null)
      setIsExpanded(false)
      clearHistory()
      historyDepthRef.current = 0
    }
  }

  // Derive canGoBack at render time from the synchronously-updated ref
  const canGoBack = historyDepthRef.current > 0

  // Compute prev/next navigation for cards (similar to quote-item.tsx)
  const columnCards = useMemo(() => {
    if (!allCurrentUserCards || !currentCard || currentCard.title) return []
    return allCurrentUserCards
      .filter((c: any) => c.status === currentCard.status && c.boardId === currentCard.boardId)
      .sort((a: any, b: any) => a.position - b.position)
  }, [allCurrentUserCards, currentCard])

  const currentCardIndex = useMemo(() => {
    if (!currentCard || currentCard.title) return -1
    return columnCards.findIndex((c: any) => c.id === currentCard.id)
  }, [columnCards, currentCard])

  const hasPrev = currentCardIndex > 0
  const hasNext = currentCardIndex >= 0 && currentCardIndex < columnCards.length - 1

  const handleNavigatePrev = () => {
    if (hasPrev) {
      const prevCard = columnCards[currentCardIndex - 1]
      setCurrentCard(prevCard)
      setCard(prevCard)
    }
  }

  const handleNavigateNext = () => {
    if (hasNext) {
      const nextCard = columnCards[currentCardIndex + 1]
      setCurrentCard(nextCard)
      setCard(nextCard)
    }
  }

  // Compute prev/next navigation for pages (use pagesByLoggedInUser and filter by boardId)
  const sortedPages = useMemo(() => {
    if (!pagesByLoggedInUser || !currentCard || !currentCard.title) return []
    return pagesByLoggedInUser
      .filter((p: any) => p.boardId === currentCard.boardId)
      .sort((a: any, b: any) => a.position - b.position)
  }, [pagesByLoggedInUser, currentCard])

  const currentPageIndex = useMemo(() => {
    if (!currentCard || !currentCard.title) return -1
    return sortedPages.findIndex((p: any) => p.id === currentCard.id)
  }, [sortedPages, currentCard])

  const hasPagePrev = currentPageIndex > 0
  const hasPageNext = currentPageIndex >= 0 && currentPageIndex < sortedPages.length - 1

  const handlePageNavigatePrev = () => {
    if (hasPagePrev) {
      const prevPage = sortedPages[currentPageIndex - 1]
      setCurrentCard(prevPage)
      setCard(prevPage)
    }
  }

  const handlePageNavigateNext = () => {
    if (hasPageNext) {
      const nextPage = sortedPages[currentPageIndex + 1]
      setCurrentCard(nextPage)
      setCard(nextPage)
    }
  }

  return (
    <>
      {modalOpen && currentCard && (
        currentCard.title ?
        <EditPageModal
          key={currentCard.id}
          pages={pages}
          page={currentCard}
          theme={theme}
          pageModalShow={modalOpen}
          setPageModalShow={closeModal}
          canGoBack={canGoBack}
          onNavigateBack={handleNavigateBack}
          hasNext={!canGoBack && hasPageNext}
          hasPrev={!canGoBack && hasPagePrev}
          onNavigateNext={handlePageNavigateNext}
          onNavigatePrev={handlePageNavigatePrev}
          isExpandedProp={isExpanded}
          setIsExpandedProp={setIsExpanded}
        />
        :
        <EditNewCard
          key={currentCard.id}
          cardTitle='Edit Card'
          isBoard={false}
          cardDetails={currentCard}
          cardModalShow={modalOpen}
          setCardModalShow={closeModal}
          canGoBack={canGoBack}
          onNavigateBack={handleNavigateBack}
          hasNext={!canGoBack && hasNext}
          hasPrev={!canGoBack && hasPrev}
          onNavigateNext={handleNavigateNext}
          onNavigatePrev={handleNavigatePrev}
          isExpandedProp={isExpanded}
          setIsExpandedProp={setIsExpanded}
        />
      )}
    </>
  )
}

export default LinkCardUpdate
